import { refreshWaiting } from './waiting'
import { supabase } from './supabase'
import type { PostOriginal } from './feed'
// A live board question, a comment and an answer can each earn stars (099).
import { nudgeStars } from './starPulse'
import type { HubLang } from './i18n'
import { ASK, type OneRequest } from './hubCopy'
import { isLinkPage } from './pages'
// The reaction vocabulary is the feed's and stays there: 058 gave replies the
// SAME four kinds through the same component, and a second copy of the enum
// mirror is how the two surfaces would end up offering different reactions.
import type { ReactionCounts, ReactionKind } from './feed'
import { requestTranslation } from './feed'
import type { PostOriginal as SourceText } from './feed'
import type { MentionMap } from './mentions'

/**
 * Wiki One — search, intake, threads, votes and curation.
 * WIK-1, WIK-2, WIK-3, plus FED-4's comments (which serve the feed too).
 *
 * ── WHY EVERY CALL HERE IS AN RPC ───────────────────────────────────────────
 *
 * The same reason 012 gave for feed_page. An answer is a parent row plus a
 * locale variant plus a side table (rule 6), which variant a viewer gets is
 * app.resolve_variant()'s six-branch chain, and supabase-js has no LATERAL. A
 * client-side `.from('content')` read would re-derive that chain in TypeScript
 * — the reimplementation 011 forbade.
 *
 * And `.from('content').insert().select()` CANNOT WORK: supabase-js appends
 * RETURNING when .select() is chained, RETURNING makes the statement a read,
 * and app.content_is_visible() re-queries the table for a row its own command
 * has not committed. It fails blaming the wrong policy. OPS-23.
 *
 * ── THE RANKING RULE IS NOT IMPLEMENTED HERE ────────────────────────────────
 *
 * Official wins, then relevance, then votes (David, 8 Sep). That lives in
 * wiki_search()'s ORDER BY where one definition governs every caller. Do not
 * sort `results` in this file — a client-side sort that disagrees with the
 * database is invisible until somebody stars something and it does not move.
 *
 * ── COUNTS ARE PUBLIC, VOTERS ARE NOT ───────────────────────────────────────
 *
 * `votes` is an aggregate from a definer function; `iVoted` is your own row
 * through a policy that shows you only your own. There is deliberately no way
 * to ask who voted, and adding one is a decision about surveillance rather
 * than a feature (018's words, and a vote is a stronger signal than a
 * reaction).
 */

export type WikiSource = 'wiki_answer' | 'wiki_question'

export type WikiResult = {
  contentId: string
  source: WikiSource
  /** The question. For an answer this is the question it answers. */
  question: string
  /** The answer body, or a question's elaboration. */
  answer: string
  /** For an answer: who owns it. For a question: WHO ASKED IT — public since 034. */
  personName: string | null
  ownerPersonId: string | null
  isOfficial: boolean
  /** Never reviewed, or not reviewed in a year. Rendered, never filtered. */
  isStale: boolean
  lastReviewedAt: string | null
  state: 'draft' | 'scheduled' | 'published' | 'archived'
  publishedAt: string | null
  /**
   * When this FIRST went out, or null if it never has (040).
   *
   * NOT interchangeable with `state`, and the difference is what Delete keys
   * on: after 040 an item can sit at `state: 'draft'` having been published
   * for a fortnight. Four rows in the live database were in exactly that
   * position on 8 Sep, which is how they got soft-deleted when a hard delete
   * was wanted. Never infer "was it ever seen" from `state`.
   */
  firstPublishedAt: string | null
  variantLocale: string
  isFallback: boolean
  /**
   * 112. 'machine' when One translated this row's question and answer from the
   * source language. The row then says so ("Translated by One") and offers the
   * original — 011's rule that machine text is visibly marked.
   */
  translationStatus: 'machine' | 'reviewed' | 'approved' | null
  votes: number
  iVoted: boolean
  replies: number
  questionStatus: 'open' | 'answered' | 'declined' | 'duplicate' | null
  /** Votes + replies in the last 7 days. Drives the Trending sort. */
  trending: number
  /** You asked it, own it, voted in it or replied to it. */
  isMine: boolean
  /**
   * 098. The content check's result as a temperature, derived IN THE DATABASE
   * by app.moderation_temperature() — the same function the filter uses, so
   * the pill and the chip cannot disagree. Never derive it here from a raw
   * status. Null only for a row read from a function that predates 098.
   */
  temperature: WikiTemperature | null
  /**
   * 187 (WIK-28). When a curator last changed this ANSWER's words after it went
   * out, or null if they never have. The row says "Updated <date>" — 011's
   * rule that a correction is visible, never silent. Read by wiki_edited_at(),
   * not wiki_search: adding a column to wiki_search's return type means a
   * drop, which the session path refuses (Traps). withEditedAt() fills it.
   */
  editedAt: string | null
}

/**
 * 098 (David, 23 Sep). What the content check concluded, as a temperature.
 * Curator mode shows it on every row and filters on it; the reader's view
 * shows neither.
 *
 * `unchecked` is pending AND failed_open, deliberately one word: neither is a
 * judgement, and colouring either like a verdict would claim the check said
 * something (QueueItem's `vpill--unchecked` makes the same call).
 */
export type WikiTemperature = 'cool' | 'warm' | 'hot' | 'unchecked'

/** The one list the pill and the filter row both walk. Order is the scale,
 *  cold to hot, then the absence of a reading. */
export const TEMPERATURES: { key: WikiTemperature; label: string; title: string }[] = [
  { key: 'cool', label: 'Cool', title: 'The content check passed this.' },
  { key: 'warm', label: 'Warm', title: 'The content check flagged this for a moderator. It can still be live.' },
  { key: 'hot', label: 'Hot', title: 'The content check blocked this. It is not on the board.' },
  {
    key: 'unchecked',
    label: 'Not checked',
    title: 'The content check has no reading for this — it was never sent, or the check was unavailable.',
  },
]

/**
 * `best` is 036's rule and the default: official, then relevance, then votes.
 * The other three are the reader's own choice and legitimately override it —
 * a sort that does not sort is worse than no sort. The crowd still cannot bury
 * an official answer, because a vote total is not a sort.
 *
 * `mine` is "things I got involved in", NOT "things about my crew". The second
 * one cannot be built: there are 0 employment rows and the only org unit
 * memberships are the build team at a legal entity. When ACC-7 lands, add a
 * separate `for_me` — do not widen this one, because they answer different
 * questions.
 */
export type WikiSort = 'best' | 'trending' | 'new' | 'mine'

export type WikiComment = {
  commentId: string
  parentCommentId: string | null
  authorPersonId: string
  authorName: string | null
  /** Null when retracted — the row survives, the words do not. */
  body: string | null
  /** 123. 'machine' when `body` is One's rendering in your language of a
   *  reply on a PRIVATE thread; null when it is the author's own words.
   *  Feed comments are never translated and are always null. */
  translationStatus: 'machine' | null
  isOfficial: boolean
  isStale: boolean
  moderationStatus: string
  isHidden: boolean
  isRetracted: boolean
  votes: number
  iVoted: boolean
  /**
   * 058. Counts by kind, and a kind nobody picked has NO KEY — absent, not
   * zero, which is 018's rule for the post above and means the same thing
   * here: "0" claims the company looked and declined.
   *
   * There is no `reactedBy` and there must not be. content_reaction is
   * self-read only and the counts come through a security-definer aggregate
   * that returns no identities — rule 8, and the same posture the feed card
   * has carried since 018.
   */
  reactions: ReactionCounts
  myReaction: ReactionKind | null
  /**
   * 059. The people this comment addressed by name, id → display name.
   *
   * The body holds `@{uuid}` tokens and never a name — see lib/mentions.ts for
   * why. Render through splitMentions(body, mentions); an id with no entry here
   * becomes "@someone" rather than a raw uuid.
   */
  mentions: MentionMap
  /**
   * 060. Tags read out of this reply's body, same `tag` table as posts — so
   * #CPT on a post and #cpt on a reply are one thing, counted once by
   * trending_tags. Empty on a retracted reply, for the same reason the body is
   * null: the words are gone, so what they were about is too.
   */
  tags: { slug: string; label: string }[]
  editedAt: string | null
  createdAt: string
}

export type WikiQuestion = {
  questionId: string
  question: string
  detail: string
  locale: string
  askedBy: string | null
  askedAt: string
  status: 'open' | 'answered' | 'declined' | 'duplicate'
  isPublished: boolean
  isOfficial: boolean
  answerId: string | null
}

/** One row as PostgREST returns it: snake_case keys, unknown values. */
type Row = Record<string, unknown>

/**
 * The curator's filters (039).
 *
 * EVERY ONE OF THESE IS A REQUEST, NOT A PERMISSION. 030 said it of
 * p_include_hidden, 031 of p_include_drafts, and it is still the thing that
 * keeps them safe to expose: wiki_search is SECURITY INVOKER and RLS decides
 * what any caller can see. An ordinary employee asking for `unpublished` gets
 * their own drafts and nothing else — not an error, a short list.
 *
 * The Wiki One screen hides these controls behind wiki.curate. That is
 * PRESENTATION and nothing may come to depend on it.
 */
export type WikiFilters = {
  /** published (default) | unpublished | all. 031's boolean could say the
   *  first two and not the third, which is the one a curator needs. */
  state?: 'published' | 'unpublished' | 'all'
  official?: 'any' | 'official' | 'unofficial'
  /** `needs` = never reviewed, or not reviewed in a year. Same definition the
   *  is_stale badge uses — app.wiki_needs_review(), one function, so the two
   *  cannot drift (039 invariant 7). */
  review?: 'any' | 'needs'
  source?: 'any' | WikiSource
  /**
   * Content I am ANSWERABLE for: answers I own, questions I asked.
   *
   * NOT the `mine` sort, which is app.is_my_thread() — anything I voted in or
   * replied to as well. A curator reviewing their own answers does not want
   * the forty threads they once upvoted. Combining the two is legal and means
   * what it says. Do not merge them into one control.
   */
  owner?: 'any' | 'mine'
  /** 098. Applied BY THE DATABASE (rule 1) — a page filtered here would be
   *  some of the matches presented as the answer. */
  temperature?: 'any' | WikiTemperature
}

/** One PostgREST row into a WikiResult. Shared by wiki_search and wiki_by_ids,
 *  which return the same shape on purpose. */
function toResult(r: Row): WikiResult {
  return {
    contentId: r.content_id as string,
    source: r.source as WikiSource,
    question: r.question as string,
    answer: r.answer as string,
    personName: (r.answered_by as string) ?? null,
    ownerPersonId: (r.owner_person_id as string) ?? null,
    isOfficial: r.is_starred as boolean,
    isStale: r.is_stale as boolean,
    lastReviewedAt: (r.last_reviewed_at as string) ?? null,
    state: r.state as WikiResult['state'],
    publishedAt: (r.published_at as string) ?? null,
    firstPublishedAt: (r.first_published_at as string) ?? null,
    variantLocale: r.variant_locale as string,
    isFallback: r.is_fallback as boolean,
    translationStatus: (r.translation_status as WikiResult['translationStatus']) ?? null,
    votes: (r.votes as number) ?? 0,
    iVoted: (r.i_voted as boolean) ?? false,
    replies: (r.replies as number) ?? 0,
    questionStatus: (r.question_status as WikiResult['questionStatus']) ?? null,
    trending: (r.trending as number) ?? 0,
    isMine: (r.is_mine as boolean) ?? false,
    temperature: (r.temperature as WikiTemperature) ?? null,
    editedAt: null,
  }
}

/**
 * 187. Fill `editedAt` for the answers in a list, from wiki_edited_at().
 *
 * A second read, on purpose (see editedAt). It is decoration: if it fails the
 * list still renders, with no "Updated" line, rather than the board going
 * blank over a date.
 */
async function withEditedAt(rows: WikiResult[]): Promise<WikiResult[]> {
  const ids = rows.filter((r) => r.source === 'wiki_answer').map((r) => r.contentId)
  if (ids.length === 0) return rows
  const { data, error } = await supabase.rpc('wiki_edited_at', { p_ids: ids })
  if (error) return rows
  const when = new Map(
    ((data ?? []) as { content_id: string; edited_at: string }[]).map((d) => [d.content_id, d.edited_at]),
  )
  return rows.map((r) => (when.has(r.contentId) ? { ...r, editedAt: when.get(r.contentId) ?? null } : r))
}

/**
 * HOW MANY ROWS THE BOARD AND SEARCH READ (6 Oct, Brand Q&A FINAL).
 *
 * It was 25, and the database capped any ask at 50. The FAQ alone is 54
 * answers, so somebody scrolling #/wiki saw 25 of them, in vote order, with
 * nothing saying there were more: REC-54's "a limited read must say it is a
 * page", broken silently. 133 raises the cap to 200 and the board asks for
 * all of it — at ~70 published rows that is every question, and the list
 * says so if it ever fills (`listCapped` in hubCopy).
 *
 * Keep this equal to wiki_search's cap (133). Asking for more is clamped
 * server-side, so a larger number here would make the capped note lie.
 */
export const WIKI_LIST_LIMIT = 200

export async function searchWiki(
  query: string,
  opts: { limit?: number; sort?: WikiSort } & WikiFilters = {},
): Promise<WikiResult[]> {
  const { data, error } = await supabase.rpc('wiki_search', {
    p_query: query,
    p_locale: null,
    p_limit: opts.limit ?? 25,
    // Named arguments throughout: 039 replaced p_include_drafts with p_state
    // in the same position, so a positional call would have kept compiling
    // and silently meant something else.
    p_state: opts.state ?? 'published',
    p_sort: opts.sort ?? 'best',
    p_official: opts.official ?? 'any',
    p_review: opts.review ?? 'any',
    p_source: opts.source ?? 'any',
    p_owner: opts.owner ?? 'any',
    p_temperature: opts.temperature ?? 'any',
  })
  if (error) throw new Error(error.message)
  return withEditedAt(((data ?? []) as Row[]).map(toResult))
}

/**
 * Asking. `body` is the question itself and is capped at 300 characters — it is
 * the headline people scan in a list; the background goes in `detail`.
 *
 * THE QUESTION WILL BE PUBLIC AND WILL CARRY THE ASKER'S NAME (David, 8 Sep).
 * This writes the DRAFT only. Since 22 Sep (096) nothing should call it on its
 * own: `postQuestion` below writes the draft AND sends it through the content
 * check, which is what puts it on the board. Called alone, the question waits
 * in the curator queue — which is still the right place for it to land if the
 * check never gets the call.
 */
export async function askQuestion(body: string, detail?: string): Promise<string> {
  const { data, error } = await supabase.rpc('ask_question', {
    p_body: body,
    p_detail: detail ?? null,
    p_locale: null,
  })
  if (error) throw new Error(error.message)
  return data as string
}

/**
 * What happened to a question sent to the community board.
 *
 *   live     on the board now (the check passed or flagged it — a flag is
 *            live AND in the moderator queue, and the asker is told nothing
 *            different: nothing happened to them, guideline 8).
 *   blocked  the check said no. It stays a draft, a moderator sees it, and
 *            `reason` is the one sentence the check wrote FOR the asker.
 *   waiting  the check could not be reached at all, so the draft sits in the
 *            curator queue exactly as every question did before 096. Honest
 *            and safe: nothing unchecked went up.
 */
export type BoardPost =
  | { state: 'live'; contentId: string; unchecked: boolean }
  | { state: 'blocked'; contentId: string; reason: string }
  | { state: 'waiting'; contentId: string }

/**
 * Put a question on the Wiki One board — 096, David 22 Sep.
 *
 * Two steps, in this order, and the order is the safety: `ask_question()`
 * writes a draft, then publish-post grades the text IT READS BACK FROM THE ROW
 * and publishes it. The same Edge Function as a post, not a second endpoint: a
 * second one would be a second place to keep the prompt, the fail-open rule and
 * the block wording right (the reason `publishDraft` in feed.ts gives, too).
 *
 * If the second step fails the first still stands — a draft in the curator
 * queue, reported as `waiting`. That is the fail-SAFE direction, deliberately
 * the opposite of a post's fail-open: a post that cannot be checked publishes
 * marked "unchecked" because the check was unavailable (B09); here the call
 * never arrived, so there is nothing to mark and a curator is the fallback.
 */
export async function postQuestion(body: string, detail?: string): Promise<BoardPost> {
  const id = await askQuestion(body, detail)
  try {
    const { data, error } = await supabase.functions.invoke('publish-post', {
      body: { content_id: id, publish: true, kind: 'wiki_question' },
    })
    if (error) return { state: 'waiting', contentId: id }
    const res = (data ?? {}) as Record<string, unknown>
    if (res.blocked === true) {
      return {
        state: 'blocked',
        contentId: id,
        reason: String(res.reason ?? 'It appears to hit one of the content guidelines.'),
      }
    }
    if (res.error || !res.content_id) return { state: 'waiting', contentId: id }
    nudgeStars()
    return { state: 'live', contentId: id, unchecked: res.unchecked === true }
  } catch {
    return { state: 'waiting', contentId: id }
  }
}

export async function loadQuestionQueue(
  status: WikiQuestion['status'] = 'open',
): Promise<WikiQuestion[]> {
  const { data, error } = await supabase.rpc('question_queue', {
    p_status: status,
    p_limit: 50,
  })
  if (error) throw new Error(error.message)
  return ((data ?? []) as Row[]).map((r) => ({
    questionId: r.question_id as string,
    question: r.question as string,
    detail: r.detail as string,
    locale: r.locale as string,
    askedBy: (r.asked_by as string) ?? null,
    askedAt: r.asked_at as string,
    status: r.status as WikiQuestion['status'],
    isPublished: r.is_published as boolean,
    isOfficial: r.is_official as boolean,
    answerId: (r.answer_id as string) ?? null,
  }))
}

/** B18: curation is the review, so this publishes with no model check in front. */
/**
 * Take a published Wiki One item back off the board (040).
 *
 * NOT retire and NOT hide. Retire is final and answers-only; hide is a
 * MODERATOR judging a colleague's words and keeps the item `published`,
 * because it went out. This is comms saying "we are not saying this while we
 * fix it", and it is reversible — publishing again writes a second human
 * verdict.
 *
 * There is deliberately no equivalent for a POST. A post is a dated utterance:
 * it happened, people read it, and the remedy is a takedown rather than a
 * pretence that it never published. `first_published_at` survives either way.
 */
export async function unpublishWikiContent(contentId: string, note?: string): Promise<void> {
  const { error } = await supabase.rpc('unpublish_wiki_content', {
    p_content_id: contentId,
    p_note: note ?? null,
  })
  if (error) throw new Error(error.message)
}

/**
 * Delete a Wiki One question or answer (041).
 *
 * Returns 'deleted' for a HARD delete — the item was never published, nobody
 * saw it, and it is gone with its variant, replies and votes — or 'removed'
 * for a soft delete of something that had gone out.
 *
 * **The caller must tell the person which one the button will do BEFORE they
 * press it**, from `firstPublishedAt`. The two are not interchangeable and the
 * word "Delete" over a soft delete is a lie about the click.
 *
 * An author may hard-delete their own never-published question; anything that
 * went out needs wiki.curate. That is enforced in the function, which is
 * SECURITY DEFINER with no DELETE policy behind it — its internal checks are
 * the entire authorisation boundary (021), so nothing here is a gate.
 */
export async function deleteWikiContent(
  contentId: string,
  reason?: string,
): Promise<'deleted' | 'removed'> {
  const { data, error } = await supabase.rpc('delete_wiki_content', {
    p_content_id: contentId,
    p_reason: reason ?? null,
  })
  if (error) throw new Error(error.message)
  return (data as 'deleted' | 'removed') ?? 'removed'
}

export async function publishWikiContent(contentId: string): Promise<void> {
  const { error } = await supabase.rpc('publish_wiki_content', { p_content_id: contentId })
  if (error) throw new Error(error.message)
  // 112. A published ANSWER is translated into the languages the company reads.
  // Not awaited: the curator's toggle must not wait on a model, and a
  // translation that does not arrive is a fallback, never an error. The Edge
  // Function decides what is translatable (a question is not, today).
  void requestTranslation(contentId)
}

/**
 * A curator changes the words of a Wiki One answer (187, WIK-28).
 *
 * A REPLACE: question AND answer, both sent every time (rule 9). The database
 * refuses anyone without unscoped wiki.curate — the owner included (David,
 * 12 Oct) — a question, a removed or retired answer, blanks and an edit that
 * changes nothing; it marks the answer reviewed and drops the machine Spanish.
 * Then, as publishWikiContent does, ask One to translate the new words. Not
 * awaited: a translation that does not arrive is a fallback, never an error.
 */
export async function editWikiAnswer(contentId: string, question: string, answer: string): Promise<void> {
  const { error } = await supabase.rpc('edit_wiki_answer', {
    p_content_id: contentId,
    p_question: question,
    p_answer: answer,
  })
  if (error) throw new Error(error.message)
  void requestTranslation(contentId)
}

/** The source-language question and answer behind a translated row (112). */
export async function loadWikiOriginal(contentId: string): Promise<SourceText | null> {
  const { data, error } = await supabase.rpc('wiki_original', { p_content_id: contentId })
  if (error) throw error
  const row = (data as { title: string | null; body: string | null; locale: string }[] | null)?.[0]
  if (!row) return null
  return { title: row.title ?? '', body: row.body ?? '', locale: row.locale }
}

export async function answerQuestion(
  questionId: string,
  question: string,
  answer: string,
  ownerPersonId?: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('answer_question', {
    p_question_id: questionId,
    p_question: question,
    p_answer: answer,
    p_owner: ownerPersonId ?? null,
  })
  if (error) throw new Error(error.message)
  return data as string
}

export async function declineQuestion(questionId: string, note: string): Promise<void> {
  const { error } = await supabase.rpc('close_question', {
    p_question_id: questionId,
    p_status: 'declined',
    p_note: note,
    p_answer_id: null,
  })
  if (error) throw new Error(error.message)
}

export type CurateAction = 'star' | 'unstar' | 'review' | 'retire' | 'restore'

/**
 * The asymmetry is 031b's and it is enforced in the database, not here: an
 * OWNER may 'review' their own answer; only wiki.curate may star, retire or
 * reassign. No pre-check here, because a UI check that disagrees with the
 * trigger is worse than none — it makes a button that looks live and fails.
 */
export async function curateAnswer(contentId: string, action: CurateAction): Promise<void> {
  const { error } = await supabase.rpc('curate_wiki_answer', {
    p_content_id: contentId,
    p_action: action,
  })
  if (error) throw new Error(error.message)
}

export async function curateQuestion(
  contentId: string,
  action: 'official' | 'unofficial' | 'review',
): Promise<void> {
  const { error } = await supabase.rpc('curate_question', {
    p_content_id: contentId,
    p_action: action,
  })
  if (error) throw new Error(error.message)
}

// ── Threads (FED-4, and the feed uses the same calls) ───────────────────────

export async function loadComments(contentId: string): Promise<WikiComment[]> {
  const { data, error } = await supabase.rpc('comments_for', {
    p_content_id: contentId,
    p_limit: 200,
  })
  if (error) throw new Error(error.message)
  return ((data ?? []) as Row[]).map((r) => ({
    commentId: r.comment_id as string,
    parentCommentId: (r.parent_comment_id as string) ?? null,
    authorPersonId: r.author_person_id as string,
    authorName: (r.author_name as string) ?? null,
    body: (r.body as string) ?? null,
    translationStatus: r.translation_status === 'machine' ? 'machine' : null,
    isOfficial: r.is_official as boolean,
    isStale: r.is_stale as boolean,
    moderationStatus: r.moderation_status as string,
    isHidden: r.is_hidden as boolean,
    isRetracted: r.is_retracted as boolean,
    votes: (r.votes as number) ?? 0,
    iVoted: (r.i_voted as boolean) ?? false,
    // `?? {}` covers a row read before 058 landed and a null from jsonb; the
    // component treats an empty object and a missing key identically, which is
    // what makes "absent, not zero" hold without a branch per kind.
    reactions: (r.reactions as ReactionCounts) ?? {},
    myReaction: (r.my_reaction as ReactionKind) ?? null,
    // 059. id → display name, resolved by the database at read time. The body
    // carries ids and never names, so this map is the only thing that can turn
    // a token into words — an id missing from it renders "@someone", which is
    // the right answer for a leaver and for a colleague the reader may not see.
    mentions: (r.mentions as MentionMap) ?? {},
    tags: (r.tags as { slug: string; label: string }[]) ?? [],
    editedAt: (r.edited_at as string) ?? null,
    createdAt: r.created_at as string,
  }))
}

/** Replies go two levels deep. A third is refused by a trigger, not by the UI. */
export async function addComment(
  contentId: string,
  body: string,
  parentCommentId?: string,
  /**
   * 127 (FED-25). Called when the content check has graded the new reply —
   * pass, flag, or taken down — so a thread can re-read and show it. Never
   * awaited by the write: the reply is already live, which is FED-4's point.
   */
  onChecked?: () => void,
): Promise<string> {
  const { data, error } = await supabase.rpc('add_comment', {
    p_content_id: contentId,
    p_body: body,
    p_parent_comment_id: parentCommentId ?? null,
  })
  if (error) throw new Error(error.message)
  refreshWaiting() // 325: a count on the nav or the bell may have moved
  nudgeStars()
  const id = data as string
  void checkReply(id).then(() => onChecked?.())
  return id
}

/**
 * 127 (FED-25). Ask the content check to grade one reply, now.
 *
 * The reply is live before this is called and stays live whatever happens
 * here: a failure is swallowed, because the two-minute sweep
 * (app.check_comments_sweep) picks up anything this misses, and an error the
 * writer can do nothing about is noise. A reply on a private support thread
 * comes back with nothing checked — those are never graded (127's header).
 */
export async function checkReply(commentId: string): Promise<void> {
  try {
    await supabase.functions.invoke('publish-post', {
      body: { kind: 'check_comments', comment_id: commentId },
    })
  } catch {
    /* see above */
  }
}

/**
 * 127. Why the check took one of YOUR replies down — guideline 8: the author
 * is told which rule, in their own language, and keeps their words. Read
 * straight from comment_verdict: its policy lets a reply's author (and a
 * moderator) read the verdict on it, and nobody else. Only blocks are asked
 * for; a pass or a flag is nothing to tell the author (guideline 8 again).
 */
export async function loadRetractionReasons(commentIds: string[]): Promise<Map<string, string | null>> {
  const out = new Map<string, string | null>()
  if (commentIds.length === 0) return out
  const { data, error } = await supabase
    .from('comment_verdict')
    .select('comment_id, reason, checked_at')
    .in('comment_id', commentIds)
    .eq('verdict', 'block')
    .eq('failed_open', false)
    .order('checked_at', { ascending: false })
  if (error) throw new Error(error.message)
  for (const r of (data ?? []) as { comment_id: string; reason: string | null }[]) {
    if (!out.has(r.comment_id)) out.set(r.comment_id, r.reason)
  }
  return out
}

/** 123. The author's own words behind a translated reply — "Show original".
 *  Shaped as a PostOriginal (no title) so TranslatedLine's one loader type
 *  serves it. Null for a reply you cannot see or one withdrawn. */
export async function loadCommentOriginal(commentId: string): Promise<PostOriginal | null> {
  const { data, error } = await supabase.rpc('comment_original', { p_comment_id: commentId })
  if (error) throw new Error(error.message)
  const row = (data as { body: string | null; locale: string }[] | null)?.[0]
  if (!row) return null
  return { title: '', body: row.body ?? '', locale: row.locale }
}

/**
 * 183. Ask publish-post to translate the replies under a post or answer that
 * are missing a reader's language. Returns how many renderings it stored.
 * Never throws: a reply that is not translated is shown as written.
 */
export async function requestCommentTranslation(contentId: string): Promise<number> {
  try {
    const { data, error } = await supabase.functions.invoke('publish-post', {
      body: { kind: 'translate_comments', content_id: contentId },
    })
    if (error) return 0
    const n = (data as { translated?: number } | null)?.translated
    return typeof n === 'number' ? n : 0
  } catch {
    return 0
  }
}

export async function retractComment(commentId: string, note?: string): Promise<void> {
  const { error } = await supabase.rpc('retract_comment', {
    p_comment_id: commentId,
    p_note: note ?? null,
  })
  if (error) throw new Error(error.message)
}

export async function moderateComment(
  commentId: string,
  action: 'hide' | 'unhide',
  note?: string,
): Promise<void> {
  const { error } = await supabase.rpc('moderate_comment', {
    p_comment_id: commentId,
    p_action: action,
    p_note: note ?? null,
  })
  if (error) throw new Error(error.message)
}

export async function markCommentOfficial(commentId: string, official = true): Promise<void> {
  const { error } = await supabase.rpc('mark_comment_official', {
    p_comment_id: commentId,
    p_official: official,
  })
  if (error) throw new Error(error.message)
}

// ── Votes ───────────────────────────────────────────────────────────────────

/**
 * Returns the new count. Idempotent server-side — a double tap on a phone with
 * one bar is the same vote arriving twice, not an error somebody has to read.
 */
export async function castVote(target: { contentId?: string; commentId?: string }): Promise<number> {
  const { data, error } = await supabase.rpc('cast_vote', {
    p_content_id: target.contentId ?? null,
    p_comment_id: target.commentId ?? null,
  })
  if (error) throw new Error(error.message)
  return (data as number) ?? 0
}

export async function withdrawVote(target: {
  contentId?: string
  commentId?: string
}): Promise<number> {
  const { data, error } = await supabase.rpc('withdraw_vote', {
    p_content_id: target.contentId ?? null,
    p_comment_id: target.commentId ?? null,
  })
  if (error) throw new Error(error.message)
  return (data as number) ?? 0
}

// ── Ask One (WIK-5) ─────────────────────────────────────────────────────────

export type OneBasis = 'direct' | 'inferred' | 'none'

/**
 * Which corpus the answer actually rests on (ask-one v6, HLP-11).
 *
 * DERIVED SERVER-SIDE from the citations that survived validation, never taken
 * from the model — so it describes what was really read rather than what was
 * claimed. The drawer renders it as the source line on the message.
 */
export type OneCorpus = 'wiki' | 'platform' | 'both' | 'none'

export type OneAnswer = {
  /** False means nothing published covers it — the escalation case. */
  found: boolean
  /**
   * How One got there (ask-one v2, 11 Sep). `direct`: an item answers it.
   * `inferred`: no item says it, the items clearly support it, and the card
   * says so — the reader is told before they act on it. `none`: not found.
   * Never inferred about pay, seniority, contracts or reporting lines; that
   * line is drawn in the prompt and this type only carries the result.
   */
  basis: OneBasis
  /** The question as asked — carried so a rating can name what was rated. */
  question: string
  answer: string
  /** The content_ids One actually drew on, rendered as the results below it. */
  used: string[]
  /**
   * The platform help entry_keys One drew on (ask-one v6, HLP-11).
   *
   * THESE ARE NOT WIKI CARDS AND MUST NEVER BE RENDERED AS ONE. They carry an
   * entry_key and no content_id, `wikiByIds()` knows nothing about them, and
   * passing one through the results list gives a citation that opens onto
   * nothing — which reads as a broken link rather than a different kind of
   * source. They get a count and a quieter line; see `sourceLineFor`.
   */
  usedHelp: string[]
  /** Where the answer came from. See OneCorpus — derived, not claimed. */
  fromCorpus: OneCorpus
  /** True when the answer leans on non-official material. Shown to the reader. */
  unofficialSources: boolean
  /** The check could not run. The lexical results still stand. */
  unavailable?: boolean
  rateLimited?: boolean
  /**
   * 126. The id of the log row this FIRST turn wrote, or null (a follow-up
   * writes none). The drawer holds it in memory to stamp the declared intent
   * and where the person went next (`stampAskOne`), and drops it on close.
   * It must never be stored anywhere else — HLP-13's third condition.
   */
  logRef?: string | null
  /**
   * v9. What the answer cites, with a title the reader can recognise, built
   * SERVER-SIDE from the citations that survived validation — so a link under
   * an answer can only ever point at something One was really given. Absent
   * from an older deployment, which is why it is optional: the drawer then
   * falls back to the source line alone.
   */
  sources?: OneSource[]
  /**
   * v10 (S4, HLP-46). The person's own screens One read to answer, DERIVED by
   * the function from the tools that ran and returned - never what the model
   * says it read. Labels are the screens' names in the reader's language; the
   * route opens the screen. Empty where the switch is off or nothing was read.
   */
  lookedAt?: OneLookedAt[]
  /**
   * v9. Set when the question could mean two or more things the material
   * answers differently. Each option is a COMPLETE standalone question, so
   * picking one asks it fresh — the vague turn and this one are left out of
   * the history (`historyFrom`) and the picked question is what gets logged.
   */
  clarify?: string[]
  /**
   * v9. How One read a loose or follow-up question, when it had to read it at
   * all ("Vehicle branding after the rebrand"). Null when the question was
   * explicit — a restatement of a clear question is friction, not care.
   */
  readingAs?: string | null
  /**
   * v9.5 (14 Oct). The message is an idea for the hub or a report of
   * something in it not working, not a question. Says WHAT it is, never where
   * it goes: the drawer reads it and offers the platform lane itself
   * (`RequestOffer`). Absent from an older deployment, which reads as null.
   */
  request?: OneRequest | null
}

/** A cited source as the server returns it (v9; 'screen' is v10's tool read;
 *  'guide_section' is S5's: a section of a training guide the guides tool
 *  RETURNED this turn and the answer cited - 'guide' stays the platform help
 *  corpus's kind, so the two are never confused). */
export type OneSource =
  | { kind: 'wiki'; id: string; title: string }
  | { kind: 'guide'; key: string; title: string; route?: string }
  | { kind: 'screen'; route: string; title: string }
  | { kind: 'guide_section'; route: string; title: string; page?: string }

/** v10. A screen One read for the person (see OneAnswer.lookedAt). */
export type OneLookedAt = { tool: string; label: string; route: string }

/** An in-app route the server named: a path, never an address off the site. */
function isAppRoute(v: unknown): v is string {
  return typeof v === 'string' && /^\/[a-z0-9/_-]{0,80}$/i.test(v)
}

/** S5. A guide section's route, and nothing else: #/help/guides/<guide>/<section>. */
export function isGuideRoute(v: unknown): v is string {
  return typeof v === 'string' && /^\/help\/guides\/[a-z0-9-]{1,60}\/[a-z0-9-]{1,60}$/.test(v)
}

/** Exported for the session store (lib/askOneSession.ts): a restored answer
 *  goes through the same checks as a fresh one. */
export function sourcesOf(raw: unknown): OneSource[] {
  if (!Array.isArray(raw)) return []
  const out: OneSource[] = []
  for (const r of raw as Array<Record<string, unknown>>) {
    const title = typeof r?.title === 'string' ? r.title.slice(0, 200) : ''
    if (r?.kind === 'wiki' && typeof r.id === 'string') out.push({ kind: 'wiki', id: r.id, title })
    // HLP-37: a help entry's page, when it names one (346); the drawer labels
    // it from lib/pages.ts and draws nothing for a route that is not there.
    else if (r?.kind === 'guide' && typeof r.key === 'string')
      out.push({ kind: 'guide', key: r.key, title, ...(isLinkPage(r.route) ? { route: r.route } : {}) })
    else if (r?.kind === 'screen' && isAppRoute(r.route) && title) out.push({ kind: 'screen', route: r.route, title })
    // SUP-2: with the page where the section's task is done, if it is one.
    else if (r?.kind === 'guide_section' && isGuideRoute(r.route) && title)
      out.push({ kind: 'guide_section', route: r.route, title, ...(isLinkPage(r.page) ? { page: r.page } : {}) })
  }
  return out
}

/** v10. `looked_at`, shape-checked: a label and an in-app route, at most 7. */
export function lookedAtOf(raw: unknown): OneLookedAt[] {
  if (!Array.isArray(raw)) return []
  const out: OneLookedAt[] = []
  for (const r of raw as Array<Record<string, unknown>>) {
    const label = typeof r?.label === 'string' ? r.label.trim().slice(0, 40) : ''
    if (label && isAppRoute(r?.route) && typeof r?.tool === 'string' && !out.some((o) => o.tool === r.tool)) {
      out.push({ tool: r.tool, label, route: r.route })
    }
  }
  return out.slice(0, 7)
}

export function clarifyOf(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const opts = (raw as unknown[])
    .filter((o): o is string => typeof o === 'string')
    .map((o) => o.trim())
    .filter((o) => o.length > 0 && o.length <= 200)
    .slice(0, 4)
  return opts.length >= 2 ? opts : undefined
}

/**
 * One turn as the drawer holds it, and as ask-one v7 expects it back.
 *
 * `text` on an assistant turn is the ANSWER ONLY. The envelope — basis, the
 * two citation lists, from_corpus — is the client contract, not something to
 * read back to the model.
 */
export type OneTurn = { role: 'user' | 'assistant'; text: string }

/**
 * The line under an answer saying what it rests on.
 *
 * IT READS BOTH LISTS, AND THAT IS THE WHOLE POINT. An answer that came purely
 * from the platform guide comes back `found: true` with `used` EMPTY — so a
 * screen that only counted `used` would render a confident answer with nothing
 * beneath it, which reads as a model answering from its own knowledge. It is
 * the opposite: a citation the screen could not see.
 *
 * Exported so the suite can assert the four cases without mounting a drawer.
 */
export function sourceLineFor(one: OneAnswer, lang: HubLang = 'en'): string | null {
  const w = one.used.length
  const h = one.usedHelp.length
  // The words are lib/hubCopy.ts's (SHL-25); the choice of which is here.
  const c = ASK[lang]
  switch (one.fromCorpus) {
    case 'wiki':
      return c.fromWiki(w)
    case 'platform':
      return c.fromGuide(h)
    case 'both':
      return c.fromBoth(w, h)
    case 'none': {
      /* v10. A tool read is not a corpus, so an answer resting only on the
         person's own screens is found with fromCorpus 'none' - and must still
         say what it rests on, or it is a confident answer with nothing
         beneath it. S5: the same for an answer resting on guide sections the
         guides tool returned (a guide is not one of the two corpora either). */
      if (!one.found) return null
      const sections = (one.sources ?? []).flatMap((s) => (s.kind === 'guide_section' ? [s.title] : []))
      const parts = [
        ...(sections.length > 0 ? [c.fromGuides(sections.slice(0, 3).join(' · '))] : []),
        ...((one.lookedAt?.length ?? 0) > 0 ? [c.fromScreens((one.lookedAt ?? []).map((l) => l.label).join(' · '))] : []),
      ]
      return parts.length > 0 ? parts.join(' — ') : null
    }
  }
}

/**
 * Ask One. There is no retrieval step — the whole corpus is ~2,250 tokens and
 * goes into the prompt entire, which is why "do I keep my truck" can reach an
 * answer that says "vehicle" when lexical search returns nothing at all.
 *
 * PUBLISHED ONLY: wiki_corpus() filters state='published' explicitly. The FAQ
 * was published 8 Sep, so One answers from it; un-publish it and One goes
 * quiet again with no separate switch.
 *
 * v2 (11 Sep) may INFER from the items and says when it has — see `basis`.
 *
 * This never throws. One being unavailable must not break the search box — the
 * lexical results are already on screen by the time this resolves, and a failed
 * summary simply does not appear.
 */
export async function askOne(
  question: string,
  opts: {
    locale?: string
    /**
     * The conversation so far, oldest first, NOT including `question`.
     *
     * The server derives "is this a follow-up" from whether this is empty and
     * logs accordingly (086) — so an empty array is a new question and a
     * non-empty one is a continuation. There is no separate flag to keep in
     * step with it, deliberately.
     *
     * It is also TRIMMED SERVER-SIDE to `ask_one_policy.history_turns`. Sending
     * everything is correct: the cap is the database's to apply, and a second
     * copy of the number here is the thing 086 exists to avoid.
     */
    history?: OneTurn[]
    /**
     * v10 (S4). The page chip, as WORDS: the route and the page's name. The
     * function puts it in the turn as context ("the person is on …") and never
     * as a permission or a filter, so it cannot narrow what One reads -
     * HLP-16's rule for hints still holds. Null when the chip is off or the
     * page has none.
     */
    page?: { route: string; title: string } | null
  } = {},
): Promise<OneAnswer> {
  try {
    const { data, error } = await supabase.functions.invoke('ask-one', {
      body: {
        question,
        locale: opts.locale ?? null,
        history: opts.history ?? [],
        ...(opts.page ? { page: opts.page } : {}),
      },
    })
    if (error) throw error
    const d = data as Record<string, unknown>
    const found = Boolean(d.found)
    const corpus = d.from_corpus
    return {
      found,
      basis: !found ? 'none' : d.basis === 'inferred' ? 'inferred' : 'direct',
      question,
      answer: String(d.answer ?? ''),
      used: Array.isArray(d.used) ? (d.used as string[]) : [],
      usedHelp: Array.isArray(d.used_help) ? (d.used_help as string[]) : [],
      // Narrowed rather than cast: an older deployment of the function does not
      // send this field at all, and `undefined as OneCorpus` would put a
      // source line on screen reading "From undefined".
      fromCorpus:
        corpus === 'wiki' || corpus === 'platform' || corpus === 'both' ? corpus : 'none',
      unofficialSources: Boolean(d.unofficial_sources),
      unavailable: Boolean(d.unavailable),
      rateLimited: Boolean(d.rate_limited),
      logRef: typeof d.log_ref === 'string' ? d.log_ref : null,
      sources: sourcesOf(d.sources),
      lookedAt: found ? lookedAtOf(d.looked_at) : [],
      clarify: clarifyOf(d.clarify),
      readingAs:
        typeof d.reading_as === 'string' && d.reading_as.trim() ? d.reading_as.trim().slice(0, 160) : null,
      request: d.request === 'idea' || d.request === 'problem' || d.request === 'technical' ? d.request : null,
    }
  } catch {
    // "Failed to send a request to the Edge Function" is supabase-js wrapping a
    // browser fetch failure — it means the request never left the browser, and
    // on a cross-origin call the first suspect is the preflight. Either way the
    // page keeps working on lexical results alone.
    return {
      found: false,
      basis: 'none',
      question,
      answer: '',
      used: [],
      usedHelp: [],
      fromCorpus: 'none',
      unofficialSources: false,
      unavailable: true,
    }
  }
}

// ── What you came for (HLP-16) ───────────────────────────────────────────────

/**
 * The four destinations somebody can arrive at the drawer already wanting.
 *
 * ── SEARCH IS NOT ONE OF THEM, AND THAT IS THE CHANGE (18 Sep) ──────────────
 *
 * "Find something in the knowledge base" was the first of five buttons and it
 * has been deleted rather than moved. Looking something up is what the drawer
 * does whether or not anybody presses anything — it is the lead sentence of
 * the welcome, not a destination — so a button for it collected two kinds of
 * person: the ones who meant it, and the ones who pressed the first row
 * because a list invites a choice. DECLARING NO INTENT *IS* THE LOOKUP INTENT,
 * and it records more honestly than a button does, which matters because the
 * whole point of capturing intent (HLP-4's routing record, HLP-14's inbox
 * label) is that the record is worth reading.
 *
 * `lane` is the support lane the intent belongs to, and the three keys that
 * have one — `platform`, `hr`, `technical` — are 085's lane keys EXACTLY: a
 * global role whose key begins `support.`, with the lane being the part after
 * the dot, and the same three values `platform_help_entry.lane` carries. They
 * match on purpose and must keep matching, because that equality is what lets
 * an unanswered question inherit the lane of the corpus that failed it. The
 * one with a null lane is not support: it is the public board.
 *
 * IDEAS ARE NOT A FIFTH LANE (David, 18 Sep). An idea about the hub goes to the
 * people who fix the hub — the same queue, distinguished by the label rather
 * than by a new role, and `support.platform` already owns the help corpus, so
 * they are the right readers. HLP-14 is one person holding all three lanes that
 * exist; a fourth would be worse rather than clearer.
 */
export type AskIntentKey = 'board' | 'platform' | 'hr' | 'technical'

export type AskIntent = {
  key: AskIntentKey
  /** On the button. What you want to do, in your words, not the team's name. */
  label: string
  /**
   * The line under the label, and it answers ONE QUESTION: who else sees this.
   *
   * That is the seam the whole design turns on (HLP-1) — not subject matter,
   * which people do not sort themselves by, but audience, which they
   * understand immediately. So "Public, with your name on it" and "Never goes
   * on a public board" are the working parts and the subject words are the
   * hint. The HR line says what the lane is ABOUT and not what will happen to
   * the question, because there is no HR write path today (HLP-3) and a line
   * promising delivery would be a lie; "never goes on a public board" is true
   * today because nothing sends, and stays true the day the door opens.
   */
  blurb: string
  /** Named back at you on the ask prompt, so the destination is not a guess. */
  prompt: string
  /**
   * "…still want to <still>?" — the second of the two questions an answer comes
   * back with. A verb phrase rather than the label, because the label is a menu
   * item and this is a sentence somebody has already half-said.
   */
  still: string
  /** 085's lane key, or null where the destination is not a support team. */
  lane: 'platform' | 'hr' | 'technical' | null
}

/**
 * Order is the order on screen: the one door that works, then the three lanes
 * HLP-3 has yet to build a write path for.
 *
 * AN INTENT IS A HINT AND MUST NEVER BECOME A FILTER. One reads BOTH corpora
 * whatever was pressed: somebody who chose "technical expert" and then asks how
 * stars work gets the stars answer, because a drawer that refuses a question it
 * can answer on the strength of a button pressed ten seconds earlier is worse
 * than no shortcut. Nothing here is passed to `askOne`, and that absence is
 * the rule.
 *
 * THE LABELS ARE THE DESTINATION, THE BLURBS ARE THE AUDIENCE. "Get a
 * technical expert" carries "The work, not the hub" because the technical lane
 * reads as IT to almost everybody — that rename is 085's and the clause is
 * what holds it, so it is asserted rather than left to a copy edit.
 */
export const ASK_INTENTS: ReadonlyArray<AskIntent> = [
  {
    key: 'board',
    label: 'Ask the OneConetec community',
    blurb: 'Anyone in the company can answer. Public, with your name on it.',
    prompt:
      'You want to put a question to the community. What is it? I will see whether it is already answered first.',
    still: 'put it to the community',
    lane: null,
  },
  {
    key: 'platform',
    label: 'Platform support',
    blurb:
      'Something in the hub broken, confusing or missing — and where ideas go.',
    prompt: 'What happened, or what would you like to see?',
    still: 'send it to platform support',
    lane: 'platform',
  },
  {
    /**
     * THE HR LANE IS BUILT AND TESTED AND HAS NO DOOR (David, 18 Sep; 089).
     *
     * Its column value, write path, read policy and assertions all exist. What
     * does not exist is a way to press it, and the reason is risk asymmetry
     * rather than effort: HR is where a read-policy mistake is worst, where
     * GDPR Article 9 arrives unannounced in a text box, where the retention
     * window has not been decided (HLP-13), and where the recipient is the
     * chain of command rather than an independent route (HLP-14).
     *
     * ── SO THE WORDING HAD TO CHANGE, NOT JUST THE PLUMBING ─────────────────
     *
     * "Ask HR privately / Never goes on a public board" READS AS AN OFFER, and
     * the person only finds out there is no route at the very end, after they
     * have typed an employment question. Three weeks of that is survivable;
     * six months across ~930 people is a broken promise.
     *
     * So the label names the SUBJECT — all of it, not three examples of it —
     * and the blurb says plainly that the route is not open, BEFORE anybody types — which is 034's rule ("the ask box
     * states all of it before anybody types") applied to an absence. One still
     * answers from the published FAQ, which is a real thing this does for you,
     * and the second sentence is where to go instead.
     *
     * `lane` stays 'hr' because it describes the DESIGN and the design has not
     * changed. `intentRouting()` is what describes the state of the build.
     */
    key: 'hr',
    /**
     * THE LABEL NAMES THE SUBJECT, AND IT NAMES THE WHOLE OF IT (David,
     * 18 Sep). It read "Pay, leave and contracts", which is three examples
     * wearing a subject's clothes: somebody with a question about a policy,
     * a benefit or the rebrand reads three nouns that are not theirs and
     * concludes this row is not for them. A row that enumerates teaches
     * people to sort themselves by keyword, which is exactly what 16 Sep
     * rejected two front doors for.
     *
     * The other three rows name a whole thing too — the community,
     * platform support, a technical expert — so this one was the odd one out.
     */
    label: 'HR and employment',
    blurb:
      'One answers from the published FAQ. There is no private HR route in the hub yet — for anything personal, your manager or HR directly.',
    prompt: 'What is your question?',
    // Not "ask HR privately" any more: nothing asks HR, so the verb has to be
    // about the thing that does happen. HLP-18's assertion refuses a Send-to
    // verb on a non-public lane and this stays well clear of one. "Look at"
    // survived the relabel deliberately: "ask about HR" would read as asking
    // HR, and there is nobody at the other end of that.
    still: 'look at HR and employment questions',
    lane: 'hr',
  },
  {
    key: 'technical',
    label: 'Get a technical expert',
    blurb:
      'Field data, testing, equipment, client deliverables. The work, not the hub.',
    prompt: 'What is your question?',
    still: 'reach a technical expert',
    lane: 'technical',
  },
]

export function askIntent(key: AskIntentKey | null): AskIntent | null {
  return ASK_INTENTS.find((i) => i.key === key) ?? null
}

/* `intentActionLabel` (HLP-18) named the button that left One for a person.
   S2 (31 Oct, HLP-44) removed that button: Ask One links to #/help, and the
   ticket form names each team by its intent row. Deleted with its last caller. */

/**
 * What the confirm step can actually do with a pre-declared intent TODAY.
 *
 * A FUNCTION rather than a fifth column, because it describes the state of the
 * build and changes as lanes open, while `lane` describes the design and does
 * not. 089 moved two of the four:
 *
 *   'public'   the community board. `postQuestion()`, named, live the moment
 *              the content check passes it (096 — it used to wait for a
 *              curator).
 *   'lane'     a real private send. `create_support_question()` exists, the
 *              read policy exists, and `support_lanes()` says somebody holds
 *              it. Platform and technical, as of 089.
 *   'fallback' the lane is real but NOBODY HOLDS IT, so there is no door and
 *              the board is a reasonable alternative the person may take or
 *              leave. A bug report or a technical question on a public board is
 *              a normal thing.
 *   'hold'     no door, and the board is NOT an alternative. An employment
 *              question must never be nudged onto a board carrying the asker's
 *              name in front of ~900 colleagues — B15's k=6 says pseudonymity
 *              does not survive 57 BUs, and this is worse than pseudonymous,
 *              it is signed.
 *
 * ── THE SECOND ARGUMENT IS WHAT KEEPS "NO EMPTY DOOR" TRUE ──────────────────
 *
 * `openLanes` is what `support_lanes()` returned. THE SERVER OWNS THE RULE that
 * a lane with no holder is absent, so this reads the absence rather than
 * re-deciding it — there is no holder count here and there must not be one.
 * Omitting the argument means "not loaded yet", and an unloaded lane is treated
 * as closed: a door rendered optimistically during a fetch is a door that
 * reaches nobody for as long as the fetch takes.
 *
 * HR IS 'hold' WHATEVER `openLanes` SAYS, and that is checked first
 * deliberately. Its role is staffed today (085 granted all three to one
 * person), so a lane-driven answer would open a door David's 18 Sep call says
 * must not open — the gate on HR is the decision, not the staffing.
 *
 * A DOOR THAT REACHES NOBODY IS WORSE THAN A MISSING DOOR: the person believes
 * they have asked somebody and nothing ever tells them otherwise.
 */
export function intentRouting(
  key: AskIntentKey | null,
  openLanes?: readonly string[],
): 'public' | 'lane' | 'fallback' | 'hold' {
  if (key === 'hr') return 'hold'
  if (key === 'platform' || key === 'technical') {
    return openLanes?.includes(key) ? 'lane' : 'fallback'
  }
  return 'public'
}

/**
 * THE INTENTS A PERSON IS OFFERED — every one that has a door (David, 23 Sep:
 * "we don't want people to find dead ends").
 *
 * HR was listed in the drawer's opening menu and in the routing step's "Where
 * should this go?" list, and pressing it led only to "There is no private HR
 * route yet". A row whose only outcome is an explanation that it goes nowhere
 * is a dead end with a label on it. So a `hold` intent is NOT OFFERED.
 *
 * DERIVED FROM `intentRouting`, never a second list (rule 11). The hold is a
 * decision, and deleting its line there is what turns the HR lane on; if this
 * were its own list, turning HR on would leave the row hidden and nobody would
 * know why. `fallback` IS offered: a lane nobody holds still lands on a working
 * alternative (the community), which is not a dead end.
 *
 * `ASK_INTENTS` stays whole, because `askIntent()` resolves labels from it and
 * the lane keys must keep matching 085 and `platform_help_entry.lane`.
 */
export const OFFERED_INTENTS: ReadonlyArray<AskIntent> = ASK_INTENTS.filter(
  (i) => intentRouting(i.key) !== 'hold',
)

/** True when some intent exists but is held back — the routing list says, in
 *  one sentence and with no button, where an employment question goes. */
export const SOME_INTENT_HELD = OFFERED_INTENTS.length < ASK_INTENTS.length

// ── Rating One's answer (048) ────────────────────────────────────────────────

export type OneReason = 'not_my_question' | 'wrong' | 'vague' | 'unsourced' | 'other'

/**
 * The five reasons a thumbs-down may carry. A LIST, not a text box: free text
 * would be a fifth field of personal data and a surface nothing moderates
 * (FED-25). The sixth reason is "Post the question", which the card offers
 * beside these because a reason with a posted question attached is worth ten
 * reasons alone. Order is the order on screen: commonest first.
 */
export const ONE_REASONS: ReadonlyArray<{ key: OneReason; label: string }> = [
  { key: 'not_my_question', label: "Didn't answer what I asked" },
  { key: 'wrong', label: 'Wrong or out of date' },
  { key: 'vague', label: 'Too vague to act on' },
  { key: 'unsourced', label: "I'd want a person's name on this" },
  { key: 'other', label: 'Something else' },
]

/**
 * Thumbs up or down on One's answer — never on a human's (036's upvote is the
 * signal there, and a down on a colleague's answer is the downvote 036
 * refused). Writes a row with the query, the answer, the basis and the cited
 * ids and NO person; the event log carries the person and no text. Never
 * throws: a rating button that errors is one nobody presses twice, and the
 * page has nothing to do with the failure anyway.
 */
export async function rateOne(
  one: OneAnswer,
  verdict: 'up' | 'down',
  reason?: OneReason,
): Promise<boolean> {
  if (!one.found || one.basis === 'none') return false
  try {
    const { error } = await supabase.rpc('rate_one_answer', {
      p_query: one.question,
      p_basis: one.basis,
      p_answer: one.answer,
      p_used: one.used,
      p_verdict: verdict,
      p_reason: verdict === 'down' ? reason ?? null : null,
      // 126: which guide the rated answer came from — `used` holds Wiki One ids only.
      p_corpus: one.fromCorpus === 'none' ? null : one.fromCorpus,
    })
    return !error
  } catch {
    return false
  }
}

// ── Comms signals: MOVED (126) ───────────────────────────────────────────────
// wiki_signals() and wiki_feedback_totals() were retired with the #/wiki
// Signals card. What people asked One is read on #/support/ask-one, through
// lib/askOneDesk.ts — one reader of one log.

/** The items One cited, in the order it cited them. */
export async function wikiByIds(ids: string[]): Promise<WikiResult[]> {
  if (ids.length === 0) return []
  const { data, error } = await supabase.rpc('wiki_by_ids', {
    p_ids: ids,
    p_locale: null,
  })
  if (error) throw new Error(error.message)
  const rows = await withEditedAt(((data ?? []) as Row[]).map(toResult))
  const order = new Map(ids.map((id, i) => [id, i]))
  return rows.sort((a, b) => (order.get(a.contentId) ?? 0) - (order.get(b.contentId) ?? 0))
}

export async function questionQueueCount(): Promise<number> {
  const { data, error } = await supabase.rpc('question_queue_count')
  if (error) throw new Error(error.message)
  return (data as number) ?? 0
}
